
import { useEffect, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import {Table, TableBody, TableCell, TableHead, TableFooter, TableRow, Paper, Typography, Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Autocomplete, FormControl} from '@mui/material';

import { formatMoney, dateToString } from '../../shared/formatters';
import { loadStart } from '../../redux/balancePerAccountSlice';
import { addTransaction } from '../../redux/transactionSlice';
import { downloadCSV } from '../../shared/utils';
import Filter from './filter';

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: '100%',
  overflowX: 'auto',
  marginTop: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(2),
  },
}));

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 700,
  [theme.breakpoints.down('sm')]: {
    minWidth: 'auto',
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: 'rgba(0, 98, 105, 0.05)',
  '& .MuiTableCell-head': {
    fontWeight: 700,
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: '0.938rem',
  fontWeight: 500,
  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 98, 105, 0.04)',
  },
}));

const MoneyCell = styled(TableCell)(({ theme }) => ({
  fontSize: '0.938rem',
  fontWeight: 500,
  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
  whiteSpace: 'nowrap',
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  position: 'relative',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));

const UserTableTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1rem',
  color: theme.palette.text.primary,
  whiteSpace: 'nowrap',
}));

const UpdateBalanceButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  textTransform: 'none',
  fontSize: '0.75rem',
  padding: '4px 8px',
  height: '28px',
  borderRadius: '12px',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  width: '100%',
}));

function showUserTable(userKey, result, onOpenModal) {
  const userData = result.users[userKey];
  const accountKeys = Object.keys(userData.accounts);
  return <StyledPaper key={userKey}>
    <StyledTable>
      <StyledTableHead>
        <TableRow>
          <StyledTableCell>
            <UserTableTitle>{userData.user.name}</UserTableTitle>
          </StyledTableCell>
          <StyledTableCell align="right">
            Saldo
          </StyledTableCell>
        </TableRow>
      </StyledTableHead>
      <TableBody>
        {accountKeys.filter(key => parseFloat(Math.abs(userData.accounts[key].total)).toFixed(0) !== '0').map(accountKey =>
          <StyledTableRow hover key={accountKey}>
            <StyledTableCell component="th" scope="row">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {userData.accounts[accountKey].account.name}
                <UpdateBalanceButton 
                  variant="outlined" 
                  size="small"
                  onClick={() => onOpenModal(accountKey, userData.accounts[accountKey], userKey)}
                >
                  Atualizar
                </UpdateBalanceButton>
              </Box>
            </StyledTableCell>
            <MoneyCell align="right">{formatMoney(userData.accounts[accountKey].total)}</MoneyCell>
          </StyledTableRow>
        )}
      </TableBody>
      <TableFooter>
        <StyledTableRow hover>
          <StyledTableCell sx={{ fontWeight: 700 }}>
            Total
          </StyledTableCell>
          <MoneyCell align="right" sx={{ fontWeight: 700 }}>{formatMoney(userData.total)}</MoneyCell>
        </StyledTableRow>
      </TableFooter>
    </StyledTable>
  </StyledPaper>;
}

function Report() {
  const dispatch = useDispatch();
  const [openModal, setOpenModal] = useState(false);
  const [newBalance, setNewBalance] = useState('');
  const [description, setDescription] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Redux state selectors
  const shouldBeUpdated = useSelector(state => state.balancePerAccount.shouldBeUpdated);
  const lastUpdate = useSelector(state => state.balancePerAccount.lastUpdate);
  const result = useSelector(state => state.balancePerAccount.result);
  const categories = useSelector(state => state.category.items);
  
  // Redux actions
  const loadStartAction = useCallback(() => dispatch(loadStart()), [dispatch]);

  const handleOpenModal = (accountKey, accountData, userKey) => {
    setSelectedAccount({ accountKey, accountData, userKey });
    setNewBalance('');
    setDescription('');
    setSelectedCategory(null);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedAccount(null);
    setNewBalance('');
    setDescription('');
    setSelectedCategory(null);
    setErrorMessage('');
  };

  const handleUpdateBalance = async () => {
    if (newBalance && selectedAccount && selectedCategory && description) {
      try {
        const currentBalance = selectedAccount.accountData.total;
        const balanceDifference = parseFloat(newBalance) - currentBalance;
        
        // Validate category type matches balance difference sign
        if (balanceDifference < 0 && selectedCategory.type !== 'debit') {
          setErrorMessage('Categoria deve ser do tipo débito para diferença de saldo negativa');
          return;
        }
        if (balanceDifference > 0 && selectedCategory.type !== 'credit') {
          setErrorMessage('Categoria deve ser do tipo crédito para diferença de saldo positiva');
          return;
        }
        
        setErrorMessage('');
        
        const transactionItem = {
          id: 0,
          description: description,
          value: Math.abs(balanceDifference),
          categoryId: selectedCategory.id,
          financeAccountId: selectedAccount.accountData.account.id,
          date: dateToString(new Date()),
        };
        
        await dispatch(addTransaction(transactionItem)).unwrap();
        handleCloseModal();
        // Refresh the balance per account data
        dispatch(loadStart());
      } catch (error) {
        console.error('Error creating transaction:', error);
      }
    }
  };

  const handleExport = () => {
    const columnsMap = [
      { field: 'user', title: 'Responsável' },
      { field: 'account', title: 'Conta' },
      { field: 'balance', title: 'Saldo' },
    ];

    // Transform the result data into a flat array for export
    const exportData = [];
    
    if (result && result.users) {
      Object.keys(result.users).forEach((userKey) => {
        const userData = result.users[userKey];
        const accountKeys = Object.keys(userData.accounts);
        
        accountKeys.forEach((accountKey) => {
          const accountData = userData.accounts[accountKey];
          exportData.push({
            user: userData.user.name,
            account: accountData.account.name,
            balance: accountData.total,
          });
        });

        // Add total row for user
        if (accountKeys.length > 0) {
          exportData.push({
            user: userData.user.name + ' (Total)',
            account: '',
            balance: userData.total,
          });
        }
      });

      // Add grand total row
      exportData.push({
        user: 'Todos Responsáveis (Total)',
        account: '',
        balance: result.total,
      });
    }

    if (exportData.length > 0) {
      downloadCSV(exportData, columnsMap, 'relatorio_saldo_por_conta.csv');
    }
  };

  useEffect(() => {
    if (shouldBeUpdated || new Date(lastUpdate).getTime() + 1000 * 10  < new Date().getTime()) {
      loadStartAction();
    }
  }, [shouldBeUpdated, lastUpdate, loadStartAction]);
  if (!result.users)
    return <div>No data</div>;
  var users = Object.keys(result.users);
  return (
    <>
      <HeaderContainer>
        <Typography variant="h4" component="h2" sx={{ 
          fontWeight: 700,
          color: '#1a1a1a',
          margin: 0,
          whiteSpace: 'nowrap',
        }}>
          Saldo por conta
        </Typography>
        <Filter onExport={handleExport}></Filter>
      </HeaderContainer>

      {(users || []).map((userKey) => showUserTable(userKey, result, handleOpenModal))}

      <StyledPaper>
        <StyledTable>
          <StyledTableHead>
            <TableRow>
              <StyledTableCell>
                <UserTableTitle>Todos Responsáveis</UserTableTitle>
              </StyledTableCell>
              <StyledTableCell align="right">
                Saldo
              </StyledTableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            <StyledTableRow hover>
              <StyledTableCell component="th" scope="row" sx={{ fontWeight: 700 }}>
                Total
              </StyledTableCell>
              <MoneyCell align="right" sx={{ fontWeight: 700 }}>{formatMoney(result.total)}</MoneyCell>
            </StyledTableRow>
          </TableBody>
        </StyledTable>
      </StyledPaper>

      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>Atualizar Saldo da Conta</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {errorMessage && (
            <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 500 }}>
              {errorMessage}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mb: 1 }}>
            {selectedAccount && `Conta: ${selectedAccount.accountData.account.name}`}
          </Typography>
          <StyledTextField
            autoFocus
            fullWidth
            label="Descrição"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            variant="outlined"
            placeholder="Ex: Ajuste de saldo inicial"
            required
          />
          <StyledTextField
            fullWidth
            label="Novo Saldo"
            type="number"
            value={newBalance}
            onChange={(e) => setNewBalance(e.target.value)}
            variant="outlined"
            inputProps={{ step: '0.01' }}
            required
          />
          <StyledFormControl>
            <Autocomplete
              value={selectedCategory}
              onChange={(event, newValue) => {
                setSelectedCategory(newValue);
              }}
              options={categories || []}
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderInput={(params) => (
                <StyledTextField {...params} label="Categoria" placeholder="Selecione uma categoria" required />
              )}
            />
          </StyledFormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancelar</Button>
          <Button onClick={handleUpdateBalance} variant="contained" color="primary">
            Atualizar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Report;